import {
  Radio,
  NumberInput,
  Group,
  Button,
  Text,
  Stack,
  Box,
  Divider,
  Tabs,
} from '@mantine/core'
import { Block, BlockNoteEditor, BlockSchema } from '@packages/blocknote-core'
import { useCallback, useState } from 'react'

export type RepeatFrequency = 'day' | 'week' | 'month' | 'quarter' | 'year'

export type RepeatTaskEventDetail = {
  originalTask: string
  times: number
  frequency: RepeatFrequency
  batchId: string // Unique identifier for this batch of repeats
}

type RepeatPanelProps<BSchema extends BlockSchema> = {
  block: Block<BSchema>
  editor: BlockNoteEditor<BSchema>
  closeMenu: () => void
}

export function RepeatPanel<BSchema extends BlockSchema>({
  block,
  editor,
  closeMenu,
}: RepeatPanelProps<BSchema>) {
  const [selectedFrequency, setSelectedFrequency] =
    useState<RepeatFrequency>('day')
  const [times, setTimes] = useState<number>(7)

  const handleRepeat = useCallback(() => {
    repeatTask(selectedFrequency, times, block, editor)
    closeMenu()
  }, [selectedFrequency, times, block, editor, closeMenu])

  return (
    <Tabs.Panel value='repeat' p='xs' style={{ maxWidth: 300 }}>
      <Radio.Group
        value={selectedFrequency}
        onChange={(value) => {
          setSelectedFrequency(value as RepeatFrequency)
        }}
        name='repeat-frequency'
      >
        <Stack spacing='xs'>
          <Radio value='day' label='Every Day' />
          <Radio value='week' label='Every Week' />
          <Radio value='month' label='Every Month' />
          <Radio value='quarter' label='Every Quarter' />
          <Radio value='year' label='Every Year' />
        </Stack>
      </Radio.Group>

      <Box mt='md'>
        <Text size='xs' weight={700}>
          FOR
        </Text>
        <Group mt='xs' spacing='xs'>
          <NumberInput
            value={times}
            onChange={(value) => {
              setTimes(value ?? 2)
            }}
            min={2}
            style={{ width: 45 }}
            size='xs'
          />
          <Text size='xs' color='dimmed'>
            {selectedFrequency.charAt(0).toUpperCase() +
              selectedFrequency.slice(1)}
            (s) - incl. current note
          </Text>
        </Group>
      </Box>

      <Divider my='sm' />

      <Text size='xs' color='dimmed' className='fs-xs'>
        This will copy the selection to multiple notes (minimum 2). Delete the
        full line of a single repeat to delete all repeats.
      </Text>

      <Button fullWidth color='orange' onClick={handleRepeat} mt='md'>
        Repeat
      </Button>
    </Tabs.Panel>
  )
}

function generateBatchId(): string {
  return `repeat-${String(Date.now())}-${Math.random().toString(36).slice(2, 10)}`
}

function repeatTask<BSchema extends BlockSchema>(
  frequency: RepeatFrequency,
  times: number,
  block: Block<BSchema>,
  editor: BlockNoteEditor<BSchema>
) {
  if (times <= 1) return

  // Generate a unique batch ID for tracking this set of repeats
  const batchId = generateBatchId()

  // Add the repeat tag to the current task
  const repeatTag = `@repeat(1/${String(times)})`
  addTextToBlock(block, editor, repeatTag)

  // Get the task content
  const taskContent = getBlockTextContent(block)

  // Create and dispatch a custom event to be handled by the main app
  const repeatEvent = new CustomEvent<RepeatTaskEventDetail>(
    'noteplan:repeatTask',
    {
      detail: {
        originalTask: taskContent,
        times,
        frequency,
        batchId,
      },
      bubbles: true,
      cancelable: true,
    }
  )

  // Dispatch the event from a DOM element that's likely to be in the document
  document.dispatchEvent(repeatEvent)
}

// Helper function to get block text content
function getBlockTextContent<BSchema extends BlockSchema>(
  block: Block<BSchema>
): string {
  // Determine task marker based on block type
  let marker = ''
  if (block.type === 'taskListItem') {
    // Marker could be * or - depending on the user settings
    marker = BlockNoteEditor.taskItemCharacters[0] + ' '
  } else if (block.type === 'checkListItem') {
    marker = '+ '
  }

  let content = ''

  for (const item of block.content) {
    if ('text' in item) {
      content += item.text
    }
  }

  // Prepend task marker to content
  return `${marker}${content}`
}

// Helper function to add text to a block
function addTextToBlock<BSchema extends BlockSchema>(
  block: Block<BSchema>,
  editor: BlockNoteEditor<BSchema>,
  text: string
) {
  // Set the cursor into the block
  if (typeof editor._tiptapEditor.commands.focus === 'function') {
    editor._tiptapEditor.commands.focus()
  }

  if (typeof editor.setTextCursorPosition === 'function') {
    editor.setTextCursorPosition(block, 'end')
  }

  const existingContent = [...block.content]

  // Check if the last block ends with a space, then don't add an extra space
  const lastContentItem = existingContent.at(-1)
  const textToAdd =
    lastContentItem &&
    'text' in lastContentItem &&
    lastContentItem.text.endsWith(' ')
      ? text
      : ` ${text}`

  existingContent.push({
    attrs: {},
    styles: {
      mention: true,
    },
    text: textToAdd,
    type: 'text',
  })

  editor.updateBlock(block, { ...block, content: existingContent })
}
